import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { CreateProcedureBody } from '@/types/patient-chart-api';

/**
 * POST /api/Patients/CreateProcedure
 *
 * Wire body curl-verified by Andrew 2026-05-20. See
 * `CreateProcedureBody` type doc for the Procedures-specific gotchas
 * (most notably: `dataAbsentReason: ""` empty string, both `recorder`
 * AND `asserter` populated, `compilation` PascalCase typo preserved).
 *
 * Cache: invalidates `['patient-procedures', patientId]`. SignalR
 * `UpdatePatientChat` "Procedure" event from PROD-827 Slice 3 (PR #202)
 * also fires for concurrent sessions.
 */

interface CreateProcedureParams {
  patientId: string;
  body: CreateProcedureBody;
}

export function useCreateProcedure() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateProcedureParams>({
    mutationFn: ({ body }) =>
      post<string>('/api/Patients/CreateProcedure', body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-procedures', patientId]);
    },
  });
}
